import { useField } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';

import { ErrorMessage } from '@components';
import type { TextAreaInputProps } from '@types';
import { cn } from '@utils';

export const BaseTextAreaInput: React.FC<TextAreaInputProps> = ({
  name,
  className = '',
  placeholder,
  isCorrect,
  disabled,
  adornment,
  rows = 4,
  errorClassName = '',
  value,
  onChange,
  onBlur,
  ...rest
}: TextAreaInputProps) => {
  const [field, meta] = useField(name);

  const isError = meta.error && meta.touched;

  const dynamicClasses = cn(
    'block w-full rounded-md padded-scrollbar-track',
    'border-0 ring-1 focus:ring-offset-2',
    'text-us sm:leading-6 placeholder-gray-400',
    'ring-theme-border-default focus:ring-theme-icon-lightBlue',
    disabled && 'bg-theme-primary-light',
    isError && 'ring-theme-error-main bg-red-50 focus:ring-theme-error-main',
    className,
  );

  return (
    <motion.div
      className={cn('flex flex-col h-fit relative space-y-5 w-full')}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <textarea
        autoComplete="off"
        id={name}
        rows={rows}
        placeholder={placeholder}
        name={name}
        value={value ?? field.value ?? ''}
        onChange={onChange ?? field.onChange}
        onBlur={onBlur ?? field.onBlur}
        disabled={disabled}
        aria-invalid={!!(meta.error && meta.touched)}
        aria-describedby={isError ? `${name}-error` : undefined}
        className={dynamicClasses}
        {...rest}
      />
      {adornment && adornment}

      <AnimatePresence>
        {isError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ErrorMessage isError={isError} errorClassName={errorClassName} meta={meta} />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
